import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, ShoppingCart, Users, QrCode } from "lucide-react";
import PurchaseListManager from "@/components/business/orders/PurchaseListManager";
import { useAuth } from "@/hooks/useAuth";

const PurchaseListPage: React.FC = () => {
  const { user } = useAuth();

  if (!user || !user.roles.includes("Apoderado")) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-red-600 mb-4">
                Acceso Restringido
              </h2>
              <p className="text-gray-600">
                Solo los apoderados pueden acceder a la gestión de listas de
                compra.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Gestión de Listas de Compra
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Administra tus compras y asigna productos a tus hijos
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          Apoderado: {user.nombre} {user.apellido}
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Listas de Compra
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  <span id="total-purchase-lists">-</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Asignaciones
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  <span id="total-assignments">-</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <QrCode className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  QR Activos
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  <span id="active-qr-codes">-</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="h-5 w-5" />
            <span>Gestión de Productos y Asignaciones</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PurchaseListManager />
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>¿Cómo funciona?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 dark:text-blue-400 font-bold">
                  1
                </span>
              </div>
              <h3 className="font-semibold mb-2">Compra Productos</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Realiza compras desde el catálogo y crea listas de productos
                canjeables
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-green-600 dark:text-green-400 font-bold">
                  2
                </span>
              </div>
              <h3 className="font-semibold mb-2">Asigna a Hijos</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Asigna productos específicos a cada uno de tus hijos registrados
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-purple-600 dark:text-purple-400 font-bold">
                  3
                </span>
              </div>
              <h3 className="font-semibold mb-2">Genera QR</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Genera códigos QR únicos para que tus hijos puedan canjear sus
                productos
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseListPage;
